import { cn } from "@ui/lib/utils";
import { useTranslation } from "react-i18next";
import { countries } from "@modules/shared/localization/const/countries";
import { MappedLocales } from "@modules/shared/localization/const/language";
import { buttonVariants } from "@ui/components/variants/buttonVariants";
import { setDefaultOptions } from "date-fns";
import { m } from "framer-motion";
import Cookies from "js-cookie";
import { useCallback } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { LANGUAGE_KEY } from "../../../../consts/keys";

type Props = {
  isMobileView?: boolean;
};

export const LanguageSwitcherList: React.FC<Props> = ({ isMobileView }) => {
  const { t } = useTranslation("Navbar");
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleLanguageChange = useCallback(
    (key: keyof typeof countries) => {
      Cookies.set(LANGUAGE_KEY, key);
      setDefaultOptions({
        locale: MappedLocales.get(key),
      });
      // Replace the locale segment in the current path
      const segments = pathname.split("/");
      if (segments.length > 1) {
        segments[1] = key;
      }
      const newPath = segments.join("/") || "/";
      navigate({ to: newPath as any });
    },
    [pathname, navigate],
  );

  return (
    <m.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className={cn(
        "flex flex-col text-tny -tracking-[0.02em] text-text-1",
        !isMobileView &&
          "absolute right-0 z-[100] -mt-2 w-full min-w-max gap-3 rounded-xl border border-border-5 bg-background-7 py-4 shadow-[0_10px_40px_0] shadow-[#00000014]",
        isMobileView && "gap-3 overflow-y-auto",
      )}
    >
      {Object.entries(countries).map(([key, country], index: number) => (
        <m.button
          key={country.label}
          type="button"
          className={cn(
            buttonVariants({ dark: "ghost" }),
            "py-[11px] !font-primary duration-150 hover:bg-background-6",
          )}
          onClick={() => handleLanguageChange(key as keyof typeof countries)}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2, delay: 0.05 * index }}
        >
          <div
            className={cn(
              "flex w-full items-center gap-3 px-4",
              isMobileView && "!p-0",
            )}
          >
            <img
              src={country.flagSrc}
              alt=""
              width={24}
              height={24}
              className="h-6 w-6 rounded-full object-cover"
              loading="lazy"
              aria-hidden="true"
            />
            <p>{t(`countries.${country.label}` as any)}</p>
          </div>
        </m.button>
      ))}
    </m.div>
  );
};
